import React from 'react';
import { MoreVertical, Zap, LogOut } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Button } from './ui/button';

/**
 * Compact "⋮" overflow menu used in every staff prep panel header.
 *
 * Houses low-frequency actions (Quick Links, Sign Out, optional extras)
 * so they don't compete visually with the task-critical buttons (view
 * toggle, notification bell, alert prefs). Keeps every panel header
 * looking uniform.
 *
 * Props:
 *   onOpenQuickLinks: () => void
 *   onLogout: () => void
 *   extraItems?: Array<{ id, label, icon: LucideIcon, onClick, danger? }>
 */
export default function StaffHeaderMenu({ onOpenQuickLinks, onLogout, extraItems = [] }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="text-zinc-300 hover:text-white hover:bg-white/5 border border-white/10 h-9 w-9 p-0 rounded-sm flex items-center justify-center"
          data-testid="staff-header-menu-trigger"
          title="More"
        >
          <MoreVertical className="w-4 h-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        sideOffset={6}
        className="bg-bg-secondary border border-white/10 rounded-sm min-w-[10rem]"
      >
        {extraItems.length > 0 && (
          <>
            {extraItems.map(item => {
              const Icon = item.icon;
              return (
                <DropdownMenuItem
                  key={item.id}
                  onSelect={() => item.onClick && item.onClick()}
                  className={`font-sans text-sm cursor-pointer ${item.danger ? 'text-red-400 focus:text-red-300 focus:bg-red-500/10' : 'text-zinc-200 focus:text-white focus:bg-white/5'}`}
                  data-testid={`staff-header-menu-${item.id}`}
                >
                  {Icon && <Icon className="w-4 h-4 mr-2" />}
                  {item.label}
                </DropdownMenuItem>
              );
            })}
            <DropdownMenuSeparator className="bg-white/10" />
          </>
        )}
        <DropdownMenuItem
          onSelect={() => onOpenQuickLinks && onOpenQuickLinks()}
          className="text-gold focus:text-gold focus:bg-gold/10 font-sans text-sm cursor-pointer"
          data-testid="staff-header-menu-quick-links"
        >
          <Zap className="w-4 h-4 mr-2" /> Quick Links
        </DropdownMenuItem>
        <DropdownMenuSeparator className="bg-white/10" />
        <DropdownMenuItem
          onSelect={() => onLogout && onLogout()}
          className="text-red-400 focus:text-red-300 focus:bg-red-500/10 font-sans text-sm cursor-pointer"
          data-testid="staff-header-menu-logout"
        >
          <LogOut className="w-4 h-4 mr-2" /> Sign Out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
